import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/Button';
import { Dialog } from 'primereact/dialog';
import { Skeleton } from 'primereact/skeleton';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';

import Layout from '../../Layout/Layout';
import axiosInstance from '../../../config/Axios';
import PromoDesignerDialog from './components/PromoDesignerDialog';
import MenuHighlightDialog from './components/MenuHighlightDialog';
import { useGetPromos, useDeletePromo } from '../../../queries/promoDesigner';
import { useCreatePlaylist } from '../../../queries/wallPlaylistLibrary';
import { useCreateAssignment } from '../../../queries/wallAssignments';
import { useGetVideoWalls } from '../../../queries/videoWall';
import { useGetWallGroups } from '../../../queries/wallGroups';
import { useCustomToast } from '../../Contexts/ToastContext';

/**
 * AI Promo Designer — the Promotions gallery (Phase 2B). Restaurant owners
 * generate finished promo posters from a one-line promotion and play them on the
 * video wall. Each saved promo can be played on a wall/group (one-item full-wall
 * playlist + assignment + start — the proven image-on-wall path) or deleted.
 */
// Map a saved promo's aspect to an orientation chip (icon + i18n label key).
const orientationOf = (aspect) => {
  if (aspect === '9:16') return { icon: 'pi pi-mobile', key: 'video_wall.promo_ai.portrait' };
  if (aspect === '1:1') return { icon: 'pi pi-stop', key: 'video_wall.promo_ai.square' };
  return { icon: 'pi pi-desktop', key: 'video_wall.promo_ai.landscape' };
};

function TargetRow({ active, icon, name, sub, onClick }) {
  return (
    <button type="button" onClick={onClick}
      className="d-flex align-items-center gap-2 w-100 text-start p-2"
      style={{ background: active ? 'var(--pp-btn-honey-soft, rgba(217,138,43,.12))' : 'transparent',
        border: `1px solid ${active ? 'var(--pp-btn-honey)' : 'var(--pp-border)'}`, borderRadius: 10, cursor: 'pointer' }}>
      <i className={icon} style={{ color: 'var(--pp-text-secondary)' }} />
      <span className="flex-fill"><span className="d-block" style={{ color: 'var(--pp-text-primary)' }}>{name}</span>
        {sub && <span className="small" style={{ color: 'var(--pp-text-secondary)' }}>{sub}</span>}</span>
      {active && <i className="pi pi-check" style={{ color: 'var(--pp-btn-honey)' }} />}
    </button>
  );
}

export default function VideoWallPromos() {
  const { t } = useTranslation();
  const { showToast } = useCustomToast();
  const { data: promos = [], isLoading } = useGetPromos();
  const del = useDeletePromo();
  const createPlaylist = useCreatePlaylist();
  const createAssignment = useCreateAssignment();
  const { data: walls = [] } = useGetVideoWalls();
  const { data: groups = [] } = useGetWallGroups();

  const [designerOpen, setDesignerOpen] = useState(false);
  const [menuHlOpen, setMenuHlOpen] = useState(false);
  const [playPromo, setPlayPromo] = useState(null); // the promo being assigned
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const openPlay = (promo) => { setTarget(null); setPlayPromo(promo); };

  const doPlay = async () => {
    if (!playPromo || !target) return;
    setBusy(true);
    try {
      const plRes = await createPlaylist.mutateAsync({
        name: `${playPromo.headline || playPromo.title || 'Promo'}`,
        items: [{ content_type: 'full_wall', media_file_id: playPromo.media_id, duration_seconds: 15, fit_mode: 'crop' }],
      });
      const body = plRes?.data?.data ?? plRes?.data;
      const playlistId = body?.playlist?.id;
      if (!playlistId) throw new Error('no playlist');

      await createAssignment.mutateAsync(
        target.kind === 'group'
          ? { group_id: target.id, playlist_id: playlistId }
          : { wall_id: target.id, playlist_id: playlistId },
      );
      const startUrl = target.kind === 'group' ? `/wall-groups/${target.id}/start` : `/video-walls/${target.id}/start`;
      try { await axiosInstance.post(startUrl); } catch { /* assignment still set */ }

      showToast('success', t('video_wall.promo_ai.title'), t('video_wall.promo_ai.playing_toast', { name: target.name }));
      setPlayPromo(null);
    } catch {
      // individual hooks toast their own errors
    } finally {
      setBusy(false);
    }
  };

  const confirmDelete = (promo) => confirmDialog({
    message: t('video_wall.promo_ai.delete_confirm'),
    header: t('video_wall.promo_ai.delete_title'),
    icon: 'pi pi-exclamation-triangle',
    acceptClassName: 'p-button-danger',
    accept: () => del.mutate(promo.id),
  });

  return (
    <Layout>
      <ConfirmDialog />
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h4 className="mb-0" style={{ color: 'var(--pp-text-primary)' }}>
            <i className="pi pi-megaphone me-2" style={{ color: 'var(--pp-primary)' }} />{t('video_wall.promo_ai.title')}</h4>
          <p className="small mb-0" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.promo_ai.subtitle')}</p>
        </div>
        <div className="d-flex gap-2">
          <Button outlined icon="pi pi-images" label={t('video_wall.menu_highlight.cta')} onClick={() => setMenuHlOpen(true)} />
          <Button icon="pi pi-sparkles" label={t('video_wall.promo_ai.create')} onClick={() => setDesignerOpen(true)} />
        </div>
      </div>

      {isLoading ? (
        <div className="row g-3">
          {[1, 2, 3].map((i) => <div key={i} className="col-12 col-sm-6 col-lg-4"><Skeleton height="288px" borderRadius="12px" /></div>)}
        </div>
      ) : promos.length === 0 ? (
        <div className="text-center py-5">
          <i className="pi pi-megaphone" style={{ fontSize: 48, color: 'var(--pp-text-secondary)' }} />
          <h6 className="mt-3 mb-1" style={{ color: 'var(--pp-text-primary)' }}>{t('video_wall.promo_ai.empty_title')}</h6>
          <p className="small mb-3" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.promo_ai.empty_hint')}</p>
          <div className="d-flex gap-2 justify-content-center">
            <Button outlined icon="pi pi-images" label={t('video_wall.menu_highlight.cta')} onClick={() => setMenuHlOpen(true)} />
            <Button icon="pi pi-sparkles" label={t('video_wall.promo_ai.create')} onClick={() => setDesignerOpen(true)} />
          </div>
        </div>
      ) : (
        <div className="row g-3">
          {promos.map((p) => (
            <div key={p.id} className="col-12 col-sm-6 col-lg-4">
              <div className="pp-card-clickable" style={{ border: '1px solid var(--pp-border)', borderRadius: 12, overflow: 'hidden', background: 'var(--pp-bg-secondary)' }}
                role="button" tabIndex={0}
                onClick={() => openPlay(p)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openPlay(p); } }}>
                <div style={{ position: 'relative', height: 240, background: 'var(--pp-bg-tertiary, rgba(0,0,0,.05))' }}>
                  <img src={p.thumb && p.thumb !== 'loading' ? p.thumb : p.path} alt={p.headline || ''}
                    style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                  <span className="d-inline-flex align-items-center gap-1" style={{ position: 'absolute', top: 8, [document?.dir === 'rtl' ? 'left' : 'right']: 8,
                    background: 'rgba(0,0,0,.65)', color: '#fff', fontSize: 11, fontWeight: 600,
                    padding: '3px 8px', borderRadius: 6, lineHeight: 1.4 }}>
                    <i className={orientationOf(p.aspect).icon} style={{ fontSize: 11 }} />
                    {t(orientationOf(p.aspect).key)}
                  </span>
                </div>
                <div className="p-2">
                  <div className="text-truncate" style={{ color: 'var(--pp-text-primary)' }} dir="auto">{p.headline || p.title || '—'}</div>
                  <div className="pp-card-actions mt-2">
                    <Button size="small" className="flex-fill" icon="pi pi-play" label={t('video_wall.promo_ai.play')}
                      onClick={(e) => { e.stopPropagation(); openPlay(p); }} />
                    <Button className="pp-card-del" text rounded severity="danger" size="small" icon="pi pi-trash"
                      aria-label={t('video_wall.promo_ai.delete_title')}
                      onClick={(e) => { e.stopPropagation(); confirmDelete(p); }} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <PromoDesignerDialog visible={designerOpen} onHide={() => setDesignerOpen(false)} onSaved={() => {}} />
      <MenuHighlightDialog visible={menuHlOpen} onHide={() => setMenuHlOpen(false)} onSaved={() => {}} />

      {/* Play-on-wall target picker */}
      <Dialog visible={!!playPromo} onHide={() => setPlayPromo(null)} style={{ width: 'min(94vw, 460px)' }}
        header={<span><i className="pi pi-play me-2" style={{ color: 'var(--pp-primary)' }} />{t('video_wall.promo_ai.play_title')}</span>}>
        <p className="small mb-2" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.promo_ai.play_hint')}</p>
        <div className="d-flex flex-column gap-2" style={{ maxHeight: 280, overflowY: 'auto' }}>
          {groups.map((g) => (
            <TargetRow key={g.id} active={target?.kind === 'group' && target.id === g.id} icon="pi pi-th-large"
              name={g.name} sub={g.orientation} onClick={() => setTarget({ kind: 'group', id: g.id, name: g.name })} />
          ))}
          {walls.map((w) => (
            <TargetRow key={w.id} active={target?.kind === 'wall' && target.id === w.id} icon="pi pi-desktop"
              name={w.name} sub={`${w.rows}×${w.columns} · ${w.orientation}`} onClick={() => setTarget({ kind: 'wall', id: w.id, name: w.name })} />
          ))}
          {walls.length === 0 && groups.length === 0 && (
            <p className="small text-center py-3" style={{ color: 'var(--pp-text-secondary)' }}>{t('video_wall.promo_ai.no_walls')}</p>
          )}
        </div>
        <div className="d-flex justify-content-end gap-2 mt-3">
          <Button text label={t('video_wall.promo_ai.cancel')} onClick={() => setPlayPromo(null)} disabled={busy} />
          <Button icon="pi pi-play" label={t('video_wall.promo_ai.play_now')} disabled={!target || busy} loading={busy} onClick={doPlay} />
        </div>
      </Dialog>
    </Layout>
  );
}
